import { CloseFullscreen, Feed, MoreHoriz } from '@mui/icons-material'
import { Box, Button, Paper, Typography } from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery'
import React, { RefObject } from 'react'
import { connect } from 'react-redux'
import AvatarWithLoader from 'components/avatar-with-loader'
import NivosModal from 'components/nivos-modal'
import NivosPaginator from 'components/nivos-paginator'
import {
  Operations as LayoutOperations,
  operations as layoutOperations
} from 'states/dux/layout/operations'
import { Payload as LayoutPayload } from 'states/dux/layout/types'
import {
  Operations as ThemeOperations,
  operations as themeOperations
} from 'states/dux/theme/operations'
import { Payload as ThemePayload } from 'states/dux/theme/types'
import State from 'states/types'
import { NivosNews } from 'types/nivos-news'
import {
  get,
  mapOperationsToDispatchProps,
  objectifyDateStringFields
} from 'utils/common'

interface StateProps {
  theme: ThemePayload
  layout: LayoutPayload
}

interface DispatchProps {
  operations: {
    theme: ThemeOperations
    layout: LayoutOperations
  }
}

type AppProps = StateProps & DispatchProps

const RecentNewsPaper: React.FC<{
  news: NivosNews
  callback?: () => any
}> = props => {
  const isMobileView = useMediaQuery('(max-width:1205px)')
  const { news } = props
  const [titleWidth, setTitleWidth] = React.useState('initial')
  const titleContainer: RefObject<HTMLElement> = React.createRef()

  React.useEffect(() => {
    if (titleContainer.current !== null) {
      setTitleWidth(
        `${titleContainer.current.getBoundingClientRect().width - 40}px`
      )
    }
  }, [titleContainer])

  return (
    <Paper elevation={8} sx={{ borderRadius: '12px', marginBottom: 1 }}>
      <Box
        display={'flex'}
        alignItems={'center'}
        width={isMobileView ? '90vw' : '700px'}
        maxWidth={'700px'}
        height={'150px'}
      >
        <Box padding={2}>
          <Typography variant={'h4'}>{news.date.getDate()}</Typography>
          <Typography variant={'h4'}>
            {news.date.toLocaleString('default', { month: 'short' })}
          </Typography>
        </Box>
        {!isMobileView && (
          <Box padding={1} height={100}>
            <AvatarWithLoader
              src={news.images.length > 0 ? news.images[0].url : ''}
              LoaderIcon={Feed}
              avatarProps={{
                sx: {
                  height: 100,
                  width: 100,
                  borderRadius: 2
                }
              }}
            />
          </Box>
        )}
        {isMobileView && (
          <>
            <Box
              paddingLeft={1}
              borderRight={1}
              height={'100px'}
              color={'darkgray'}
            />
            <Box
              paddingRight={1}
              borderLeft={1}
              height={'100px'}
              color={'darkgray'}
            />
          </>
        )}
        <Box ref={titleContainer} padding={1} flexGrow={1}>
          <Typography
            variant={'h4'}
            whiteSpace={'nowrap'}
            width={titleWidth}
            textOverflow={'ellipsis'}
            overflow={'hidden'}
            gutterBottom
          >
            {news.title}
          </Typography>
          <Typography
            variant={'body2'}
            height={55}
            maxWidth={titleWidth}
            overflow={'auto'}
          >
            {news.message}
          </Typography>
        </Box>
      </Box>
    </Paper>
  )
}

const NewsHistoryItem: React.FC<{ news: NivosNews }> = props => {
  const { news } = props
  return (
    <Box
      border={1}
      borderRadius={2}
      marginBottom={1}
      padding={1}
      height={75}
      display={'flex'}
      flexDirection={'row'}
    >
      <Box padding={2}>
        <Typography>{news.date.getDate()}</Typography>
        <Typography>
          {news.date.toLocaleString('default', { month: 'short' })}
        </Typography>
      </Box>
      <Box width={'fit-content'} padding={1}>
        <AvatarWithLoader
          src={news.images.length > 0 ? news.images[0].url : ''}
          LoaderIcon={Feed}
          avatarProps={{
            sx: {
              height: 60,
              width: 60,
              borderRadius: 2
            }
          }}
        />
      </Box>
      <Box padding={1} maxHeight={75} overflow={'auto'}>
        <Typography variant="h5">
          {news.title} {news.active && '( Active )'}
        </Typography>
        <Typography variant="body2">{news.message}</Typography>
      </Box>
    </Box>
  )
}

const NewsHistoryModal: React.FC<{
  open: boolean
  setOpen: (_: boolean) => void
}> = props => {
  const { open, setOpen } = props
  const [newsHistory, setNewsHistory] = React.useState<NivosNews[]>([])

  React.useEffect(() => {
    void get('/notification/news_history/')
      .then((data: NivosNews[]) => {
        setNewsHistory(data.map(item => objectifyDateStringFields(item)))
      })
      .catch(e => console.log(e))
  }, [])

  return (
    <NivosModal title="News History" open={open} setOpen={setOpen}>
      <Box
        minHeight={600}
        sx={{ maxWidth: '92vw', overflowX: 'auto', margin: 'auto' }}
      >
        <NivosPaginator
          itemsPerPage={5}
          dataList={newsHistory}
          childElement={NewsHistoryItem}
          childPropSetter={(data, index) => {
            return { news: data }
          }}
        />
      </Box>
    </NivosModal>
  )
}

const ImportantNewsFC: React.FC<any> = (props: AppProps) => {
  const { layout } = props
  const [activeNews, setActiveNews] = React.useState<NivosNews[]>([])
  const [viewHistory, setViewHistory] = React.useState(false)
  const isMobileView = useMediaQuery('(max-width:600px)')

  React.useEffect(() => {
    void get('/notification/active_news/')
      .then((data: NivosNews[]) => {
        setActiveNews(data.map(item => objectifyDateStringFields(item)))
      })
      .catch(e => console.log(e))
  }, [])

  return (
    <Box display={'flex'} flexDirection={'column'} alignItems={'center'}>
      <Typography variant={isMobileView ? 'h4' : 'h3'} gutterBottom>
        {layout.language.translations.importantNews}
      </Typography>
      <Box
        width={'45vw'}
        display={'flex'}
        flexDirection={'column'}
        alignItems={'center'}
      >
        {activeNews.map((news, index) => (
          <RecentNewsPaper news={news} key={index} />
        ))}
        {activeNews.length === 0 && (
          <Typography variant="h4" marginY={3}>
            No News
          </Typography>
        )}
        <Box width={'fit-content'} paddingBottom={1}>
          <Button
            variant={'outlined'}
            startIcon={viewHistory ? <CloseFullscreen /> : <MoreHoriz />}
            sx={{ borderRadius: 4 }}
            onClick={() => setViewHistory(!viewHistory)}
          >
            News history
          </Button>
        </Box>
        <NewsHistoryModal open={viewHistory} setOpen={setViewHistory} />
      </Box>
    </Box>
  )
}

const mapStateToProps = (state: State): StateProps => {
  return {
    theme: state.theme,
    layout: state.layout
  }
}

const mapDispatchToProps = mapOperationsToDispatchProps({
  theme: themeOperations,
  layout: layoutOperations
})

const ImportantNews = connect(
  mapStateToProps,
  mapDispatchToProps
)(ImportantNewsFC) as React.FC

export default ImportantNews
